import React, { useEffect } from "react";
import { Box, Typography, Grid, TextField } from "@mui/material";
import TextareaAutosize from 'react-textarea-autosize';
import { NumericFormat } from "react-number-format";
import OrderPaymentCollection from "../OrderPaymentCollectionModal";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/setup/redux/RootReducer";
import { fetchOrderTransactions, clearOrderTransactions } from "../../redux/OrderRedux";
import { sharedFormStyles } from "@/__core/styles/sharedFormStyles";
import { OrderFormData } from "../../models/OrderModel";

const parseNumericInput = (rawValue: string, thousandSep: string, decimalSep: string): number => {
  if (!rawValue) return 0;

  const decimalIndex = decimalSep ? rawValue.lastIndexOf(decimalSep) : -1;
  let integerPart = decimalIndex !== -1 ? rawValue.substring(0, decimalIndex) : rawValue;
  let decimalPart = decimalIndex !== -1 ? rawValue.substring(decimalIndex + 1) : '';

  if (thousandSep) {
    const escaped = thousandSep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    integerPart = integerPart.replace(new RegExp(escaped, 'g'), '');
  }

  const cleaned = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

interface OrderSummaryProps {
  formData: OrderFormData;
  totalAmount: number;
  additionalFees: Array<{ id: string; name: string; amount: number }>;
  grandTotal: number;
  disabled?: boolean;
  onInputChange: (field: keyof OrderFormData, value: any) => void;
  onAdditionalFeesChange: (fees: Array<{ id: string; name: string; amount: number }>) => void;
  orderId?: string;
  contextCompanyId?: string;
  onPaymentCollected?: (transaction: any) => void;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  formData,
  totalAmount,
  grandTotal,
  disabled = false,
  onInputChange,
  orderId,
  contextCompanyId,
  onPaymentCollected,
}) => {
  const dispatch = useDispatch();
  const { orderTransactions: transactions } = useSelector(
    (state: RootState) => state.orders
  );

  // Fetch transactions khi orderId thay đổi
  useEffect(() => {
    if (!orderId) {
      dispatch(clearOrderTransactions());
      return;
    }

    dispatch(fetchOrderTransactions(orderId));
  }, [orderId, dispatch]);

  const handlePaymentCollected = async (transaction: any) => {
    // Fetch lại transactions từ server để có dữ liệu mới nhất
    if (orderId) {
      dispatch(fetchOrderTransactions(orderId));
    }

    if (onPaymentCollected) {
      onPaymentCollected(transaction);
    }
  };


  const toNumber = (value: any): number => {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Chỉ tính các giao dịch thu có thực thu > 0 để hiển thị mục "Đã thu"
  // API trả status dạng UUID nên không thể lọc bằng status === 'approved'.
  const incomeTransactions = transactions.filter((t: any) => {
    const isIncome = t?.transaction_type_category === 'income' || t?.transaction_type?.category === 'income';
    const actualAmount = toNumber(t?.actual_amount);
    return isIncome && actualAmount > 0;
  });

  const totalCollected = incomeTransactions.reduce(
    (sum: number, t: any) => sum + toNumber(t?.actual_amount),
    0
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatCurrency = (amount: number | string) => {
    return new Intl.NumberFormat('vi-VN').format(typeof amount === 'string' ? parseFloat(amount) : amount);
  };

  const canShowPaymentCollection = Number(totalAmount) > 0;

  return (
    <Box sx={sharedFormStyles.noteSectionWrapper}>
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          {/* Order Notes */}
          <Box sx={{ p: 0 }}>
            <Typography sx={sharedFormStyles.compactSectionTitle}>
              Ghi chú đơn hàng
            </Typography>
            <TextareaAutosize
              className='TextareaAutosize__no-focus-ring'
              value={formData.notes || ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                if (disabled) return;
                onInputChange("notes", e.target.value);
              }}
              spellCheck={false}
              autoComplete="off"
              readOnly={disabled}
              minRows={5}
              style={sharedFormStyles.noteTextarea(!disabled)}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 6 }} sx={{ width: "100%" }}>
              <Box
                sx={sharedFormStyles.summaryRow}
              >
                <Typography
                  variant="body2"
                  sx={sharedFormStyles.summaryLabel}
                >
                  Tổng tiền hàng
                </Typography>
                <Box sx={sharedFormStyles.summaryValueContainer}>
                  <NumericFormat
                    value={totalAmount}
                    displayType="text"
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={0}
                    renderText={(value) => (
                      <Typography
                        variant="body2"
                        sx={sharedFormStyles.summaryValueText}
                      >
                        {value}
                      </Typography>
                    )}
                  />
                </Box>
              </Box>

              <Box
                sx={sharedFormStyles.summaryRow}
              >
                <Typography
                  variant="body2"
                  sx={sharedFormStyles.summaryLabel}
                >
                  Phí khảo sát
                </Typography>
                <Box sx={sharedFormStyles.summaryInputContainer}>
                  <NumericFormat
                    value={formData.survey_fee_amount || 0}
                    onChange={(event) => {
                      if (disabled) return;
                      const rawValue = event.target.value;
                      const amount = parseNumericInput(rawValue, '.', ',');
                      onInputChange("survey_fee_amount", amount);
                    }}
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    customInput={TextField}
                    size="small"
                    fullWidth
                    variant="outlined"
                    disabled={disabled}
                    sx={sharedFormStyles.summaryNumericInput}
                  />
                </Box>
              </Box>
              <Box
                sx={sharedFormStyles.summaryRow}
              >
                <Typography
                  variant="body2"
                  sx={sharedFormStyles.summaryLabel}
                >
                  Phí vận chuyển, lắp đặt
                </Typography>
                <Box sx={sharedFormStyles.summaryInputContainer}>
                  <NumericFormat
                    value={formData.shipping_installation_fee_amount || 0}
                    onChange={(event) => {
                      if (disabled) return;
                      const rawValue = event.target.value;
                      const amount = parseNumericInput(rawValue, '.', ',');
                      onInputChange("shipping_installation_fee_amount", amount);
                    }}
                    decimalScale={2}
                    thousandSeparator="."
                    decimalSeparator=","
                    customInput={TextField}
                    size="small"
                    fullWidth
                    variant="outlined"
                    disabled={disabled}
                    sx={sharedFormStyles.summaryNumericInput}
                  />
                </Box>
              </Box>


              <Box
                sx={sharedFormStyles.summaryRow}
              >
                <Typography
                  variant="body2"
                  sx={sharedFormStyles.summaryLabel}
                >
                  Tổng cộng
                </Typography>
                <Box sx={sharedFormStyles.summaryValueContainer}>
                  <NumericFormat
                    value={grandTotal}
                    displayType="text"
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={0}
                    renderText={(value) => (
                      <Typography
                        variant="body2"
                        sx={sharedFormStyles.summaryValueText}
                      >
                        {value}
                      </Typography>
                    )}
                  />
                </Box>
              </Box>

              {/* Tiêu đề "Đã thu" */}
              {orderId && incomeTransactions.length > 0 && (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={sharedFormStyles.summarySuccessHeading}
                  >
                    Đã thu
                  </Typography>
                </Box>
              )}

              {/* Danh sách đã thu - Hiển thị từng mục với ngày tháng */}
              {incomeTransactions.map((transaction: any) => {
                const dateStr = transaction.actual_date || transaction.transaction_date || transaction.created_at || transaction.date;
                const formattedDate = formatDate(dateStr);
                return (
                  <Box
                    key={transaction.id}
                    sx={sharedFormStyles.summaryRow}
                  >
                    <Typography variant="body2" sx={sharedFormStyles.summarySuccessLabel}>
                      {transaction.transaction_type_name || 'Thu tiền'} {formattedDate && `(${formattedDate})`}
                    </Typography>
                    <Box sx={sharedFormStyles.summaryValueContainer}>
                      <Typography
                        variant="body2"
                        sx={sharedFormStyles.summarySuccessValue}
                      >
                        {formatCurrency(transaction.actual_amount)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}

              {/* Tổng đã thu */}
              {orderId && totalCollected > 0 && (
                <Box
                  sx={sharedFormStyles.summaryDividerRow}
                >
                  <Typography
                    variant="body2"
                    sx={sharedFormStyles.summarySuccessLabel}
                  >
                    Tổng đã thu
                  </Typography>
                  <Box sx={sharedFormStyles.summaryValueContainer}>
                    <Typography
                      variant="body2"
                      sx={sharedFormStyles.summarySuccessValueStrong}
                    >
                      {formatCurrency(totalCollected)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Còn lại = Tổng cộng - Tổng đã thu */}
              {orderId && (
                <Box
                  sx={sharedFormStyles.summaryDividerRow}
                >
                  <Typography
                    variant="body2"
                    sx={sharedFormStyles.summaryDangerLabelStrong}
                  >
                    Còn lại phải thu
                  </Typography>
                  <Box sx={sharedFormStyles.summaryValueContainer}>
                    <Typography
                      variant="body2"
                      sx={sharedFormStyles.summaryDangerValueStrong}
                    >
                      {formatCurrency(grandTotal - totalCollected)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Payment Collection Button */}
              {!disabled && canShowPaymentCollection && (
                <Box sx={{ mt: 2 }}>
                  <OrderPaymentCollection
                    orderId={orderId}
                    contextCompanyId={contextCompanyId}
                    disabled={disabled}
                    onPaymentCollected={handlePaymentCollected}
                    walletId={(formData as any).wallet_id || ''}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderSummary;
